import React, { useMemo, useState } from "react";

import { PAGE_SIZES } from "./questionHelpers";

/**
 * Show-entries / search / pagination table, standing in for the DataTables
 * "zero-configuration" tables on old/admin/editor.php and participants.php.
 */
export default function PagedTable({ headers, rows, searchKeys, renderCells, isLoading, emptyText }) {
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      searchKeys.some((key) => String(row[key] ?? "").toLowerCase().includes(q))
    );
  }, [rows, search, searchKeys]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const firstIndex = (safePage - 1) * pageSize;
  const pageRows = filtered.slice(firstIndex, firstIndex + pageSize);

  return (
    <div className="quiz-table-card themeupdate-card">
      <div className="quiz-table-controls">
        <label className="quiz-table-show">
          Show{" "}
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>{" "}
          entries
        </label>
        <label className="quiz-table-search">
          Search:{" "}
          <input
            type="search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </label>
      </div>

      <div className="quiz-table-wrap">
        <table className="quiz-table">
          <thead>
            <tr>
              {headers.map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={headers.length} className="quiz-table-empty">
                  Loading…
                </td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={headers.length} className="quiz-table-empty">
                  {emptyText}
                </td>
              </tr>
            ) : (
              pageRows.map((row, idx) => <tr key={row.id}>{renderCells(row, firstIndex + idx + 1)}</tr>)
            )}
          </tbody>
        </table>
      </div>

      <div className="quiz-table-footer">
        <span className="quiz-table-info">
          Showing {filtered.length ? firstIndex + 1 : 0} to{" "}
          {Math.min(firstIndex + pageSize, filtered.length)} of {filtered.length} entries
          {filtered.length !== rows.length ? ` (filtered from ${rows.length})` : ""}
        </span>
        <div className="quiz-table-pager">
          <button
            type="button"
            className="quiz-pager-btn"
            disabled={safePage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span className="quiz-pager-num">
            {safePage} / {totalPages}
          </span>
          <button
            type="button"
            className="quiz-pager-btn"
            disabled={safePage >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
